<template>
  <!-- DEPLOY — one row of Home's Store-health scorecard (HealthRow). Utilisation is its
       own 0–100% scale, so it gets a split in-use / idle bar, never the shared "your
       best" scale. -->
  <div class="gauge-row">
    <HealthRow
      :eyebrow="$t('gauges.eyebrow.deploy')"
      :subtitle="$t('gauges.subtitle.deploy')"
      :accent="accent"
      :disabled="isCardDisabled"
      :status="rowStatus"
      :value="flowMode === 'overstaffed' ? `${currency} ${formatMoney(flowCost)}` : flowModeLabel"
      :value-sub="flowMode === 'overstaffed' ? $t('gauges.deploy.overSub') : $t('gauges.deploy.balancedSub')"
      :value-unit="flowMode === 'overstaffed' ? $t('gauges.row.perMonth') : ''"
      :meta="rowMeta"
      :pct="flowScore"
      :pct-sub="$t('gauges.deploy.pctSub')"
      :delta="deltaLong"
      bar="split"
      :split-right-label="flowMode === 'overstaffed' ? $t('gauges.deploy.idle', { pct: 100 - flowScore }) : $t('gauges.deploy.fullyInUse')"
      @help="showExplain = true"
    >
      <template v-if="flowMode === 'overstaffed' && idleWhen" #notes>
        <span class="row-when"><i class="bi bi-calendar3"></i> {{ idleWhen }}</span>
      </template>
    </HealthRow>
    <StoreCapacityHelpModal
      :show="showExplain"
      :segment-key="segmentKey"
      :company-staff-cost="companyStaffCost"
      @close="showExplain = false"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import StoreCapacityHelpModal from './StoreCapacityHelpModal.vue'
import HealthRow from './ui/HealthRow.vue'
import { computeCapacityGauge } from '@/prioritization/gauges'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'

const props = defineProps({
  fixedWidgetsData: { type: Object, required: true },
  fixedWidgetsCompareToData: { type: Object, default: null },
  segmentKey: { type: String, default: 'default' },
  isActive: { type: Boolean, default: true },
  prevPeriodLabel: { type: String, default: 'period' },
  accent: { type: String, default: '#F28C38' },
  hasCheckoutZones: { type: Boolean, default: true },
  laneCountInteraction: { type: Number, default: 0 },
  laneCountCashier: { type: Number, default: 0 },
  currency: { type: String, default: '€' },
  // Optional company-level override for the per-hour staff cost the utilisation
  // €-figure uses. When > 0, replaces the sector default for THIS company
  // (Settings → Checkout Staff Cost). Null / non-positive → sector default.
  companyStaffCost: { type: Number, default: null },
  // One sentence saying WHEN the idle hours fall — "Thursday and Friday mornings
  // account for most of it". Null when the waste is not concentrated, which is the
  // common case and must render nothing at all.
  idleWhen: { type: String, default: null },
})

const { t } = useI18n()
const showExplain = ref(false)

// Single source of truth for the DEPLOY math — prioritization/gauges.js
// (parity-tested against the backend signals/gauges.py).
const gauge = computed(() =>
  computeCapacityGauge(props.fixedWidgetsData, props.segmentKey, {
    isActive: props.isActive,
    hasCheckoutZones: props.hasCheckoutZones,
    laneCountInteraction: props.laneCountInteraction,
    laneCountCashier: props.laneCountCashier,
    companyStaffCost: props.companyStaffCost,
  })
)

function safePct(value) {
  const v = Number(value || 0)
  if (!Number.isFinite(v) || v < 0) return 0
  return v
}

// --- Structure / disabled --------------------------------------------------
const hasInteractionZones = computed(() => props.laneCountInteraction > 0)
const hasStructure = computed(() => props.hasCheckoutZones && hasInteractionZones.value)
const isCardDisabled = computed(() => !props.isActive || !hasStructure.value)
const isLowActivity = computed(() => !!gauge.value.isLowActivity)

// --- DEPLOY = one-directional capacity UTILISATION (docs/11 collapse) -------
// balanceScore is utilisation now (100 = at/over capacity, no idle). Overstaffing
// scores below 100; the gap is idle capacity. No overloaded/bottleneck side.
const pctRounded = computed(() => gauge.value.balanceScoreRounded)
const flowScore = computed(() => Math.round(pctRounded.value || 0))
const flowMode = computed(() => gauge.value.mode || 'setup')   // setup | overstaffed | balanced
const labourWasteValue = computed(() => gauge.value.labourWasteValue)
const flowCost = computed(() => {
  const v = gauge.value.costOfImbalance
  return Number.isFinite(v) ? v : (labourWasteValue.value || 0)
})
const flowModeLabel = computed(() => {
  if (flowMode.value === 'overstaffed') return t('gauges.deploy.overstaffed')
  if (flowMode.value === 'balanced') return t('gauges.deploy.balanced')
  return t('gauges.deploy.setup')
})

// --- Previous-period delta (utilisation vs utilisation, same scale) ---------
function utilisationFromData(d) {
  if (!d) return null
  const inter = safePct(d.checkout_interaction_usage_pct)
  const cashier = safePct(d.checkout_cashier_usage_pct)
  if (!inter || !cashier) return null
  const r = (inter / 100) / (cashier / 100)
  if (!Number.isFinite(r) || r <= 0) return null
  return Math.min(100, Math.round(r * 100))
}

const prevPctRounded = computed(() =>
  props.fixedWidgetsCompareToData ? utilisationFromData(props.fixedWidgetsCompareToData) : null
)

const deltaPts = computed(() => {
  if (prevPctRounded.value == null) return null
  return Math.round(flowScore.value - prevPctRounded.value)
})

const deltaLong = computed(() => {
  if (deltaPts.value == null || !props.prevPeriodLabel) return null
  const v = deltaPts.value
  const sign = v > 0 ? '+' : v < 0 ? '−' : '±'
  const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '◆'
  return t('gauges.row.delta', { arrow, sign, abs: Math.abs(v), label: props.prevPeriodLabel })
})

// The non-figure states (not configured / low activity / setting up), as one statement.
const rowStatus = computed(() => {
  if (!hasStructure.value) return { title: t('gauges.row.notConfigured'), sub: t('gauges.deploy.notConfiguredSub') }
  if (isLowActivity.value) return { title: t('gauges.deploy.lowTitle'), sub: t('gauges.deploy.lowSub') }
  if (flowMode.value !== 'overstaffed' && flowMode.value !== 'balanced') return { title: flowModeLabel.value, sub: t('gauges.deploy.setupSub') }
  return null
})

const rowMeta = computed(() => t('gauges.row.basedOn', {
  list: `${props.laneCountInteraction} <strong>${t('gauges.zoneNames.checkoutInteraction', props.laneCountInteraction)}</strong>`,
}))

function formatMoney(value) {
  const v = Math.round(Number(value || 0))
  if (!Number.isFinite(v)) return '0'
  // English keeps the browser-locale grouping it always had; Dutch is 1.234.
  if (currentLanguage() !== 'en') return formatNumber(v)
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
</script>

<style scoped>
.row-when { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #51637a; }
.row-when .bi { color: #8a99a4; }
</style>
